import { Alert, Box, Checkbox, Group, Modal, Stack, Tooltip, UnstyledButton, rem } from '@mantine/core';
import { IconArrowBackUp, IconCheck, IconEraser, IconInfoCircle } from '@tabler/icons-react';
import { HrBulkUpdatePeopleEvidencePayload } from 'api/actions/hr-bulk-update-people-evidence/hr-bulk-update-people-evidence-payload';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import EvidenceValueInput, { EvidenceValueDefinition } from 'components/forms/evidence-field/EvidenceValueInput';
import P2Medium from 'components/typography/P2Medium';
import P3Medium from 'components/typography/P3Medium';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import { useMemo, useState } from 'react';
import { TABLET_BREAKPOINT } from 'theme/components/utils/tablet';

export type HrBulkEvidenceDefinition = EvidenceValueDefinition & {
  evidenceFieldId: number;
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
};

export interface HrBulkEvidenceTarget {
  personId: number;
  version: number;
  firstName: string;
  lastName: string;
}

type ChangeMode = 'unchanged' | 'set' | 'clear';
type EvidenceValue = string | number | boolean | null;
type BulkEvidencePatch = HrBulkUpdatePeopleEvidencePayload['evidenceFields'][number];

interface HrPeopleBulkEvidenceModalProps {
  opened: boolean;
  people: HrBulkEvidenceTarget[];
  evidenceFields: HrBulkEvidenceDefinition[];
  hasExcludedEvidenceFields: boolean;
  onClose: () => void;
  onSubmit: (patches: BulkEvidencePatch[]) => Promise<void>;
}

const valueKeyByFieldType: Record<string, keyof BulkEvidencePatch> = {
  text: 'fieldValue',
  number: 'fieldNumberValue',
  date: 'fieldDateValue',
  select: 'fieldSelectValue',
  predefined: 'fieldPredefinedValue',
  checkbox: 'fieldBooleanValue',
};

/** Identifies a missing value after the user explicitly chose to set a field. */
function isMissingReplacement(value: EvidenceValue) {
  return value === null || (typeof value === 'string' && value.trim() === '');
}

/** Builds only the explicitly selected typed evidence patches for the atomic people update. */
export function hrBulkEvidencePatches(
  evidenceFields: HrBulkEvidenceDefinition[],
  modes: Record<number, ChangeMode>,
  values: Record<number, EvidenceValue>
): BulkEvidencePatch[] {
  return evidenceFields.flatMap((definition) => {
    const mode = modes[definition.evidenceFieldId] ?? 'unchanged';
    if (mode === 'unchanged') return [];

    const valueKey = valueKeyByFieldType[definition.fieldType];
    if (!valueKey) return [];

    return [
      {
        evidenceFieldId: definition.evidenceFieldId,
        fieldType: definition.fieldType,
        [valueKey]: mode === 'clear' ? null : values[definition.evidenceFieldId],
      },
    ];
  });
}

/** Shows only mutually applicable fields and never reads existing values into the editing controls. */
export default function HrPeopleBulkEvidenceModal({
  opened,
  people,
  evidenceFields,
  hasExcludedEvidenceFields,
  onClose,
  onSubmit,
}: HrPeopleBulkEvidenceModalProps) {
  const [modes, setModes] = useState<Record<number, ChangeMode>>({});
  const [values, setValues] = useState<Record<number, EvidenceValue>>({});
  const [loading, setLoading] = useState(false);
  const [invalidSetFieldIds, setInvalidSetFieldIds] = useState<number[]>([]);
  const visiblePeople = people.slice(0, 3);
  const remainingPeople = people.slice(3);

  const patches = useMemo(() => hrBulkEvidencePatches(evidenceFields, modes, values), [evidenceFields, modes, values]);
  const canSubmit = patches.length > 0 && !loading;

  /** Clears the previous submission error without validating a field while it is being edited. */
  const clearFieldError = (evidenceFieldId: number) => {
    setInvalidSetFieldIds((current) => current.filter((fieldId) => fieldId !== evidenceFieldId));
  };

  /** Keeps the modal open after a failed batch so the frozen request can be corrected or retried. */
  const submit = async () => {
    if (!canSubmit) return;

    const invalidFieldIds = evidenceFields
      .filter(
        (definition) =>
          modes[definition.evidenceFieldId] === 'set' &&
          isMissingReplacement(values[definition.evidenceFieldId] ?? null)
      )
      .map((definition) => definition.evidenceFieldId);
    setInvalidSetFieldIds(invalidFieldIds);
    if (invalidFieldIds.length > 0) return;

    setLoading(true);
    try {
      await onSubmit(patches);
    } catch (caught) {
      panic(caught as Error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!loading) onClose();
      }}
      title={`Upraviť evidenčné polia (${people.length})`}
      size="lg"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <Stack spacing="md">
        <Group spacing={4} aria-label="Osoby zahrnuté do hromadnej úpravy">
          {visiblePeople.map((person, index) => (
            <P3Regular key={person.personId} span>
              #{person.personId} — {person.firstName} {person.lastName}
              {index < visiblePeople.length - 1 ? ',' : ''}
            </P3Regular>
          ))}
          {remainingPeople.length > 0 && (
            <Tooltip
              withinPortal
              events={{ hover: true, focus: true, touch: true }}
              label={
                <Stack spacing={4}>
                  {remainingPeople.map((person) => (
                    <P3Regular key={person.personId} color="white">
                      #{person.personId} — {person.firstName} {person.lastName}
                    </P3Regular>
                  ))}
                </Stack>
              }
            >
              <UnstyledButton
                aria-label={`Ďalšie osoby (${remainingPeople.length})`}
                sx={{ borderBottom: '1px dotted currentColor' }}
              >
                <P3Medium span>(+{remainingPeople.length})</P3Medium>
              </UnstyledButton>
            </Tooltip>
          )}
        </Group>
        {hasExcludedEvidenceFields && (
          <Alert icon={<IconInfoCircle size={18} />} color="blue">
            Zobrazujú sa iba evidenčné polia spoločné pre všetky vybrané osoby.
          </Alert>
        )}
        <Box
          role="table"
          aria-label="Evidenčné polia"
          sx={(theme) => ({
            '& > [role="row"]': {
              display: 'grid',
              gridTemplateColumns: '54px minmax(0, 1fr) minmax(0, 1.5fr)',
              alignItems: 'center',
              gap: theme.spacing.sm,
              padding: `${theme.spacing.sm} 0`,
              borderBottom: `1px solid ${theme.colors.gray[1]}`,
            },
            [theme.fn.smallerThan('sm')]: {
              '& > [role="row"]': { gridTemplateColumns: '44px minmax(0, 1fr) minmax(0, 1.3fr)', gap: 8 },
            },
          })}
        >
          <Box role="row">
            <Box role="columnheader">
              <P3Medium>Upraviť</P3Medium>
            </Box>
            <Box role="columnheader">
              <P3Medium>Evidenčné pole</P3Medium>
            </Box>
            <Box role="columnheader">
              <P3Medium>Hodnota</P3Medium>
            </Box>
          </Box>
          {evidenceFields.map((definition) => {
            const mode = modes[definition.evidenceFieldId] ?? 'unchanged';
            const isInvalid = invalidSetFieldIds.includes(definition.evidenceFieldId);

            return (
              <Box key={definition.evidenceFieldId} role="row">
                <Box role="cell">
                  <Checkbox
                    size="md"
                    aria-label={`Upraviť ${definition.fieldName}`}
                    checked={mode !== 'unchanged'}
                    disabled={loading}
                    onChange={(event) => {
                      const nextMode = event.currentTarget.checked ? 'set' : 'unchanged';
                      setModes((current) => ({ ...current, [definition.evidenceFieldId]: nextMode }));
                      clearFieldError(definition.evidenceFieldId);
                      if (nextMode === 'set' && values[definition.evidenceFieldId] === undefined) {
                        setValues((current) => ({
                          ...current,
                          [definition.evidenceFieldId]: definition.fieldType === 'checkbox' ? false : null,
                        }));
                      }
                    }}
                  />
                </Box>
                <Box role="cell">
                  <P2Medium sx={{ overflowWrap: 'anywhere' }}>
                    {definition.fieldName}
                    {definition.isRequired && (
                      <Box component="span" c="red.7">
                        {' '}
                        *
                      </Box>
                    )}
                  </P2Medium>
                </Box>
                <Box
                  role="cell"
                  sx={(theme) => ({
                    display: 'grid',
                    gridTemplateColumns: `minmax(0, 1fr) ${rem(40)}`,
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 0,
                    [theme.fn.smallerThan(TABLET_BREAKPOINT)]: {
                      gridTemplateColumns: `minmax(0, 1fr) ${rem(32)}`,
                    },
                  })}
                >
                  <Box sx={{ minWidth: 0 }}>
                    {mode === 'clear' ? (
                      <P3Regular color="dimmed">Hodnota sa vymaže</P3Regular>
                    ) : (
                      <EvidenceValueInput
                        definition={definition}
                        hideLabel
                        value={values[definition.evidenceFieldId] ?? null}
                        error={isInvalid ? 'Zadajte náhradnú hodnotu.' : undefined}
                        disabled={loading}
                        onChange={(value) => {
                          setValues((current) => ({ ...current, [definition.evidenceFieldId]: value }));
                          setModes((current) => ({ ...current, [definition.evidenceFieldId]: 'set' }));
                          clearFieldError(definition.evidenceFieldId);
                        }}
                      />
                    )}
                  </Box>
                  {!definition.isRequired && (
                    <Tooltip label={mode === 'clear' ? 'Zadať hodnotu' : 'Vymazať hodnotu'}>
                      <ActionIcon
                        variant="subtle-gray"
                        aria-label={`${mode === 'clear' ? 'Zadať hodnotu' : 'Vymazať hodnotu'} ${definition.fieldName}`}
                        disabled={loading}
                        onClick={() => {
                          setModes((current) => ({
                            ...current,
                            [definition.evidenceFieldId]: mode === 'clear' ? 'set' : 'clear',
                          }));
                          clearFieldError(definition.evidenceFieldId);
                        }}
                      >
                        {mode === 'clear' ? <IconArrowBackUp size={18} /> : <IconEraser size={18} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        <Group position="right">
          <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            leftIcon={<IconCheck size={18} stroke={1.5} />}
            loading={loading}
            disabled={!canSubmit}
            onClick={submit}
          >
            Uložiť zmeny ({people.length})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
